// src/ProfileOverview.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet"; // Import Helmet
import Banner from "./components/Banner";
import General from "./components/General";
import Notification from "./components/Notification";
import Project from "./components/Project";
import Storage from "./components/Storage";
import Upload from "./components/Upload";
import axios from "axios";
import { useAccount } from "wagmi";

const ProfileOverview = () => {
  const { socialID } = useParams();
  const [coinData, setCoinData] = useState(null);
  const [loading, setLoading] = useState(true); // For loading state
  const [error, setError] = useState(null); // For error handling
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();

  // Redirect to "1" if socialID is undefined or set to the placeholder ":socialID"
  useEffect(() => {
    if (!socialID || socialID === ":socialID") {
      navigate("/admin/trending/1", { replace: true });
    }
  }, [socialID, navigate]);

  // Fetch coin data only if socialID is valid
  useEffect(() => {
    if (!socialID || socialID === ":socialID") return;

    const fetchCoinData = async () => {
      try {
        setLoading(true);

        // Convert socialID to integer
        const identityId = parseInt(socialID, 10);
        if (isNaN(identityId)) {
          setError("Invalid socialID parameter. It must be a number.");
          return;
        }

        const response = await axios.get(`/api/socialinfo/?identity=${identityId}`);
        console.log("Full response data:", response.data);

        if (response.data.cryptos && response.data.cryptos.length > 0) {
          setCoinData(response.data.cryptos[0]);
          console.log("Fetched coin data:", response.data.cryptos[0]);
        } else {
          setError("Coin not found");
        }
      } catch (error) {
        console.error("Error fetching coin data:", error);
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          setError(`Error fetching data: ${error.response.status}`);
        } else if (error.request) {
          console.error("No response received:", error.request);
          setError("No response from server");
        } else {
          console.error("Error setting up request:", error.message);
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCoinData();
  }, [socialID]);

  // Mixpanel tracking effect moved here, **above** any conditional returns
  useEffect(() => {
    // Ensure coinData is available before trying to access coinData.name
    if (isConnected && address && coinData) {
      import("mixpanel-browser")
        .then(({ default: mixpanel }) => {
          mixpanel.init("d1c6071c36d4637fea866b97d1295dcd", {
            debug: true,
            track_pageview: false, // disable automatic pageview tracking
            persistence: "localStorage",
          });
          mixpanel.track("Visit a coin dashboard", {
            Coin: coinData.name,
          });
        })
        .catch((error) => {
          console.error("Error loading Mixpanel:", error);
        });
    }
  }, [isConnected, address, coinData]);
  
  // Conditional rendering for loading or error states
  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  console.log("socialID:", coinData.name);

  return (
    <div className="flex w-full flex-col gap-5">
      <Helmet>
        <title>{`${coinData.name} Social Influence, Market Data & Insights | CoinHeatMap`}</title>
        <meta
          name="description"
          content={`Get comprehensive social media analytics for ${coinData.name} (${coinData.symbol}). View current price, market cap, trading volume, price changes, and more. Stay updated with CoinHeatMap's real-time crypto insights.`}
        />
        <meta property="og:url" content={`https://coinheatmap.com/dashboard/admin/trending/${coinData.id}`} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${coinData.name} Analytics, Market Data & Trending Insights | CoinHeatMap`}
        />
        <meta
          property="og:description"
          content={`Get comprehensive analytics for ${coinData.name} (${coinData.symbol}). View current price, market cap, trading volume, price changes, and more. Stay updated with CoinHeatMap's real-time crypto insights.`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://coinheatmap.com/dashboard/admin/trending/${coinData.id}`} />
        <meta
          name="twitter:title"
          content={`${coinData.name} Analytics, Market Data & Insights | CoinHeatMap`}
        />
        <meta
          name="twitter:description"
          content={`Get comprehensive trending analytics for ${coinData.name} (${coinData.symbol}). View current price, market cap, trading volume, price changes, and more. Stay updated with CoinHeatMap's real-time crypto insights.`}
        />
        <meta name="twitter:site" content="@CoinHeatMap" />
        <link rel="canonical" href={`https://coinheatmap.com/dashboard/admin/trending/${coinData.id}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": `${coinData.name} Analytics`,
            "description": `Comprehensive social media metrics and market data for ${coinData.name} (${coinData.symbol}).`,
            "url": `https://coinheatmap.com/dashboard/admin/trending/${coinData.id}`,
            "mainEntity": {
              "@type": "CryptoCurrency",
              "name": coinData.name,
              "symbol": coinData.symbol,
             
            }
          })}
        </script>
      </Helmet>

      {/* First Grid: Banner, Storage, Upload */}
      <div className="w-full mt-3 flex h-fit flex-col gap-5 lg:grid lg:grid-cols-12">
        <div className="col-span-4 lg:!mb-0">
          <Banner coinData={coinData} />
        </div>

        <div className="col-span-3 lg:!mb-0">
          <Storage coinData={coinData} />
        </div>

        <div className="z-0 col-span-5 lg:!mb-0">
          <Upload coinData={coinData} />
        </div>
      </div>

      {/* Second Grid: Project and General */}
      <div className="grid h-full grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="col-span-12 lg:col-span-4 lg:mb-0 3xl:col-span-4">
          <Project coinData={coinData} />
        </div>
        <div className="col-span-12 h-[50vh] lg:col-span-8 lg:mb-0 3xl:col-span-8">
          <General coinData={coinData} />
        </div>
      </div>
    </div>
  );
};

export default ProfileOverview;
